/**
 * OIDC configuration — Keycloak Authorization Code + PKCE S256, pure browser.
 *
 * ONE UserManager instance is shared by the React AuthProvider and the API
 * client, so token state is always consistent. Tokens live in sessionStorage;
 * silent renew uses the rotating refresh token (Keycloak client must have
 * refresh rotation enabled).
 */

import { OidcClient, UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { getConfig } from '@/config';

let userManager: UserManager | null = null;

export function getUserManager(): UserManager {
  if (!userManager) {
    const { keycloak } = getConfig();
    const origin = window.location.origin;
    userManager = new UserManager({
      authority: keycloak.authority,
      client_id: keycloak.clientId,
      redirect_uri: `${origin}/auth/callback`,
      silent_redirect_uri: `${origin}/auth/silent-renew`,
      post_logout_redirect_uri: origin,
      response_type: 'code',
      scope: 'openid profile email',
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    });
  }
  return userManager;
}

/** Strip OIDC params from the URL after the redirect callback. */
export function onSigninCallback(): void {
  window.history.replaceState({}, document.title, window.location.pathname);
}

export async function getAccessToken(): Promise<string | null> {
  const user = await getUserManager().getUser();
  return user?.access_token ?? null;
}

/**
 * Send the browser to Keycloak's SELF-REGISTRATION page (not the login page),
 * carrying the same PKCE/state as a normal sign-in so the `/auth/callback`
 * round-trip completes and `state.returnTo` is honoured afterwards.
 *
 * oidc-client-ts has no first-class "register" flow, so we build a real signin
 * request (which persists the PKCE verifier + state in sessionStorage) and then
 * swap the authorization endpoint (`.../openid-connect/auth`) for Keycloak's
 * registration endpoint (`.../openid-connect/registrations`). Requires
 * self-registration to be enabled on the realm.
 */
export async function signupRedirect(args?: {
  state?: unknown;
  login_hint?: string;
}): Promise<void> {
  const { keycloak } = getConfig();
  const origin = window.location.origin;
  // A standalone OidcClient builds a real Authorization-Code+PKCE request and
  // persists its state in the DEFAULT stateStore (localStorage) — the SAME store
  // our UserManager uses (we only override `userStore`, not `stateStore`), so
  // `signinRedirectCallback()` on `/auth/callback` resolves this request too.
  const client = new OidcClient({
    authority: keycloak.authority,
    client_id: keycloak.clientId,
    redirect_uri: `${origin}/auth/callback`,
    response_type: 'code',
    scope: 'openid profile email',
  });
  const req = await client.createSigninRequest({
    // 'si:r' marks the stored state as a signin-REDIRECT flow, exactly as
    // UserManager.signinRedirect does — without it, signinCallback() on
    // /auth/callback rejects the return with "invalid request_type in state".
    request_type: 'si:r',
    state: args?.state,
    login_hint: args?.login_hint,
  });
  const url = req.url.replace(
    '/protocol/openid-connect/auth?',
    '/protocol/openid-connect/registrations?',
  );
  window.location.assign(url);
}
